import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@/providers/RouterShim';
import type { Profile } from '../../../../@types/Profile';
import useProfile from '../../../../data/profile/useProfile';
import useSignedInBranchUser from '../../../../data/users/useSignedInBranchUser';
import useAuth from '../../../../hooks/useAuth';
import { refreshAndGetToken } from '../../../../providers/auth';
import useSnackbar from '../../../../state/Snackbar/useSnackbar';
import Avatar from '../../../../styled-components/components/Avatar';
import Foldout, { type IFoldoutItem } from '../../../../styled-components/components/Foldout';
import { SrOnly } from '../../../../styled-components/components/SrOnly';
import { urlToCfit } from '../../../../utils/cfit';
import { getInitials } from '../../../../utils/initials';
import { getDealer } from '../../../../utils/market';
import {
  FoldoutAccount,
  FoldoutButton,
  FoldoutContent,
  FoldoutHeader,
  FoldoutName,
} from './wrapper';

interface UserProps {
  externalView?: boolean;
}

const User = ({ externalView }: UserProps) => {
  const { t } = useTranslation(['common', 'header', 'snackbar']);
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { decodedToken, isAdmin, signOut } = useAuth();
  const [profile] = useProfile();
  const [organizationUser] = useSignedInBranchUser();
  const { pushSnackbarSuccess, pushSnackbarError } = useSnackbar();

  const [profileFoldout, setProfileFoldout] = useState(false);
  const onToggleProfileFoldout = () => setProfileFoldout(!profileFoldout);

  const avatarImage = useMemo(() => {
    if (!profile?.imageUrl) return undefined;
    return {
      src: urlToCfit(profile.imageUrl, { width: 225 })!,
      alt: t('profilePicture', { ns: 'common' }),
    };
  }, [profile?.imageUrl]);

  const resolveProfileName = (profile?: Profile) => {
    if (!profile?.givenName) return undefined;
    const g: string[] = [profile.givenName];
    if (profile.familyName) {
      g.push(profile.familyName);
    }
    return g.join(' ');
  };

  const userName = organizationUser?.name || resolveProfileName(profile) || decodedToken?.email;
  const initials = useMemo(() => getInitials(userName), [organizationUser, profile, decodedToken]);

  const dealerLink = getDealer();

  const copyTokenToClipboard = async () => {
    try {
      const token = await refreshAndGetToken();
      if (!token) throw new Error('Token is empty');
      await navigator.clipboard.writeText(token);
      pushSnackbarSuccess(t('copyToken.success', { ns: 'snackbar' }));
    } catch (_e) {
      pushSnackbarError(t('copyToken.error', { ns: 'snackbar' }));
    }
  };

  const onOpenBlank = useCallback(() => {
    const a = document.createElement('a');
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.href = dealerLink!;
    a.click();
  }, [dealerLink]);

  const foloutItems = useMemo(() => {
    const items: IFoldoutItem[][] = [];

    if (externalView) {
      if (decodedToken?.['belongs-to-branch']?.length) {
        items.push([
          {
            displayName: t('user.profileSettings', { ns: 'header' }),
            title: t('user.profileSettings', { ns: 'header' }),
            onClick: onOpenBlank,
          },
        ]);
      } else {
        items.push([
          {
            displayName: t('user.profileSettings', { ns: 'header' }),
            title: t('user.profileSettings', { ns: 'header' }),
            onClick: () => navigate('/profile'),
          },
        ]);
      }
    } else {
      items.push([
        {
          displayName: t('user.profileSettings', { ns: 'header' }),
          title: t('user.profileSettings', { ns: 'header' }),
          onClick: () => navigate(`/${branchId}/profile`),
          disabled: !branchId,
        },
      ]);
    }

    if (isAdmin) {
      items.push([
        {
          displayName: t('user.copyToken', { ns: 'header' }),
          title: t('user.copyToken', { ns: 'header' }),
          onClick: copyTokenToClipboard,
        },
      ]);
    }

    items.push([
      {
        displayName: t('user.logOut', { ns: 'header' }),
        title: t('user.logOut', { ns: 'header' }),
        onClick: signOut,
        tone: 'error',
      },
    ]);
    return items;
  }, [branchId, navigate, signOut, t, externalView, isAdmin]);

  return (
    <Foldout
      posX="end"
      onClose={onToggleProfileFoldout}
      isOpen={profileFoldout}
      header={
        <FoldoutHeader>
          <Avatar
            size="medium"
            tone="secondary"
            image={avatarImage}
            initials={avatarImage ? undefined : initials}
          />
          <FoldoutContent>
            <FoldoutName>{userName}</FoldoutName>
            <FoldoutAccount>{decodedToken?.email}</FoldoutAccount>
          </FoldoutContent>
        </FoldoutHeader>
      }
      items={foloutItems}
    >
      <FoldoutButton
        onClick={onToggleProfileFoldout}
        title={t('user.openButton', { ns: 'header' })}
      >
        <Avatar
          size="medium"
          tone="secondary"
          image={avatarImage}
          initials={avatarImage ? undefined : initials}
        />
        <SrOnly>{t('user.openButton', { ns: 'header' })}</SrOnly>
      </FoldoutButton>
    </Foldout>
  );
};

export default User;
